import { useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const statusColors = {
  Planning: 'bg-blue-50 text-blue-700 border-blue-200',
  Active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Paused: 'bg-amber-50 text-amber-700 border-amber-200',
  Done: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};

function ScoreBadge({ score }) {
  if (!score) return <span className="text-xs text-zinc-400">--</span>;
  const color = score >= 7 ? 'bg-emerald-100 text-emerald-700' : score >= 4 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${color}`}>{score}</span>;
}

const emptyForm = { name: '', status: 'Planning', objective: 'Awareness', start_date: '', end_date: '', budget: 0, spend: 0, linked_audience_ids: [], linked_brief_ids: [], satisfaction_score: null, learnings: '' };

export default function AdsCampaigns({ items, audiences, briefs, config, reload, hasFullAccess }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('active');

  const openCreate = () => { setEditItem(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ ...emptyForm, ...item, satisfaction_score: item.satisfaction_score || null });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.patch(`/ads/campaigns/${editItem.id}`, form);
        toast.success('Campaign updated');
      } else {
        await api.post('/ads/campaigns', form);
        toast.success('Campaign created');
      }
      setShowDialog(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await api.delete(`/ads/campaigns/${id}`);
      toast.success('Campaign deleted');
      reload();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const filtered = view === 'active'
    ? items.filter(c => c.status === 'Active' || c.status === 'Planning')
    : view === 'satisfaction'
    ? [...items].sort((a, b) => (b.satisfaction_score || 0) - (a.satisfaction_score || 0))
    : items;

  const doneBriefs = briefs.filter(b => b.status === 'Done');

  return (
    <div data-testid="ads-campaigns-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {['active', 'satisfaction', 'all'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${view === v ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
              data-testid={`campaigns-view-${v}`}>
              {v === 'active' ? 'Active' : v === 'satisfaction' ? 'By Score' : 'All'}
            </button>
          ))}
        </div>
        {hasFullAccess && (
          <Button onClick={openCreate} size="sm" className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="create-campaign-btn">
            <Plus size={14} className="mr-1" /> New Campaign
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">
          <p className="text-lg font-medium">No campaigns yet</p>
          <p className="text-sm mt-1">Create your first campaign to get started</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(camp => (
            <div key={camp.id} className="bg-white border border-zinc-200 rounded-xl p-5 hover:border-zinc-300 transition-all group" data-testid={`campaign-card-${camp.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-zinc-900 truncate">{camp.name}</h3>
                    <Badge variant="outline" className={statusColors[camp.status]}>{camp.status}</Badge>
                    <Badge variant="outline" className="bg-zinc-50 text-zinc-600 border-zinc-200">{camp.objective}</Badge>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-zinc-500">
                    {camp.start_date && (
                      <span className="flex items-center gap-1"><Calendar size={13} /> {camp.start_date}{camp.end_date ? ` - ${camp.end_date}` : ''}</span>
                    )}
                    <span>Budget: <span className="font-medium text-zinc-700">${(camp.budget || 0).toLocaleString()}</span></span>
                    <span>Spend: <span className="font-medium text-zinc-700">${(camp.spend || 0).toLocaleString()}</span></span>
                  </div>
                  {camp.learnings && <p className="text-xs text-zinc-400 mt-2 line-clamp-1">{camp.learnings}</p>}
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <ScoreBadge score={camp.satisfaction_score} />
                  {hasFullAccess && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(camp)} className="p-1.5 rounded-lg hover:bg-zinc-100" data-testid={`edit-campaign-${camp.id}`}><Pencil size={14} className="text-zinc-400" /></button>
                      <button onClick={() => handleDelete(camp.id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-campaign-${camp.id}`}><Trash2 size={14} className="text-red-400" /></button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-zinc-900">{editItem ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-700">Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Campaign name" className="bg-white text-zinc-900 border-zinc-300" data-testid="campaign-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-700">Status</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({...f, status: v}))}>
                  <SelectTrigger className="bg-white text-zinc-900 border-zinc-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{config?.campaign_statuses?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-700">Objective</Label>
                <Select value={form.objective} onValueChange={v => setForm(f => ({...f, objective: v}))}>
                  <SelectTrigger className="bg-white text-zinc-900 border-zinc-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{config?.campaign_objectives?.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-zinc-700">Start Date</Label><Input type="date" value={form.start_date || ''} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} className="bg-white text-zinc-900 border-zinc-300" /></div>
              <div><Label className="text-zinc-700">End Date</Label><Input type="date" value={form.end_date || ''} onChange={e => setForm(f => ({...f, end_date: e.target.value}))} className="bg-white text-zinc-900 border-zinc-300" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-zinc-700">Budget ($)</Label><Input type="number" value={form.budget} onChange={e => setForm(f => ({...f, budget: parseFloat(e.target.value) || 0}))} className="bg-white text-zinc-900 border-zinc-300" /></div>
              <div><Label className="text-zinc-700">Spend ($)</Label><Input type="number" value={form.spend} onChange={e => setForm(f => ({...f, spend: parseFloat(e.target.value) || 0}))} className="bg-white text-zinc-900 border-zinc-300" /></div>
            </div>
            <div>
              <Label className="text-zinc-700">Linked Audiences</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {audiences.map(a => (
                  <button key={a.id} onClick={() => setForm(f => ({...f, linked_audience_ids: f.linked_audience_ids.includes(a.id) ? f.linked_audience_ids.filter(x => x !== a.id) : [...f.linked_audience_ids, a.id]}))}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${form.linked_audience_ids.includes(a.id) ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400'}`}>
                    {a.name}
                  </button>
                ))}
                {audiences.length === 0 && <span className="text-xs text-zinc-400">No audiences created yet</span>}
              </div>
            </div>
            <div>
              <Label className="text-zinc-700">Linked Briefs (Creatives)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {doneBriefs.map(b => (
                  <button key={b.id} onClick={() => setForm(f => ({...f, linked_brief_ids: f.linked_brief_ids.includes(b.id) ? f.linked_brief_ids.filter(x => x !== b.id) : [...f.linked_brief_ids, b.id]}))}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${form.linked_brief_ids.includes(b.id) ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400'}`}>
                    {b.title}
                  </button>
                ))}
                {doneBriefs.length === 0 && <span className="text-xs text-zinc-400">No completed briefs yet</span>}
              </div>
            </div>
            <div>
              <Label className="text-zinc-700">Satisfaction Score (1-10)</Label>
              <div className="flex gap-1 mt-1">
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <button key={n} onClick={() => setForm(f => ({...f, satisfaction_score: f.satisfaction_score === n ? null : n}))}
                    className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${form.satisfaction_score === n ? (n >= 7 ? 'bg-emerald-500 text-white' : n >= 4 ? 'bg-amber-500 text-white' : 'bg-red-500 text-white') : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}`}
                    data-testid={`campaign-score-${n}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-zinc-700">Learnings</Label>
              <Textarea value={form.learnings} onChange={e => setForm(f => ({...f, learnings: e.target.value}))} placeholder="What worked, what didn't..." rows={3} className="bg-white text-zinc-900 border-zinc-300" />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="save-campaign-btn">
              {saving ? 'Saving...' : editItem ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
